import React, { useState, useMemo, useEffect } from 'react'
import {
  Grid,
  Typography,
  Box,
  MenuItem,
  Chip,
  useTheme,
  Card,
  CardContent,
  Checkbox,
  FormControlLabel,
  Tooltip,
  Radio,
  RadioGroup,
  FormControl,
  FormLabel,
} from '@material-ui/core'
import { Warning, CheckCircle, Info } from '@material-ui/icons'
import { motion, AnimatePresence } from 'framer-motion'
import { StyledTextField, pageVariants, childVariants } from '../styles/StyledComponents'
import { fetchGatkCohorts, fetchExomeDepthCohorts } from '../../../../api/analysis/cohort'
import OncotreeSelect from './OncotreeSelect'
import { alpha } from '@material-ui/core/styles'
import SpecimenFields from './SpecimenFields'
import LibraryFields from './LibraryFields'
import { EMPTY_SPECIMEN, validateSpecimen } from '../../lib/specimen'
import { EMPTY_LIBRARY, isRnaSelection, validateLibrary } from '../../lib/rnaLibrary'

// Sentinel value for the "GENESUS Automatic" cohort option in the GENESUS-CNV
// dropdown. Routes to the on-demand per-case cohort pipeline backend-side
// (translated to `cohort_id: undefined` at submission time).
export const GENESUS_AUTOMATIC_COHORT = 'genesus_automatic'

const StepConfigure = ({
  analysisType,
  selectedFiles,
  files,
  bedFiles = [],
  referenceGenome,
  onReferenceGenomeChange,
  sequencingType,
  onSequencingTypeChange,
  captureKit,
  onCaptureKitChange,
  panelName,
  onPanelNameChange,
  onValidationChange, // New prop for validation state
  runCnv = false,
  onRunCnvChange,
  cnvMethod = 'genesus-cnv', // New prop for CNV method
  onCnvMethodChange, // New prop for CNV method change
  selectedCohortId = GENESUS_AUTOMATIC_COHORT, // New prop for selected cohort ID
  onSelectedCohortIdChange, // New prop for cohort selection change
  onGatkCohortsChange, // New prop for GATK cohorts change
  onDefaultCohortChange, // New prop for default cohort change
  onExomedepthCohortsChange, // New prop for ExomeDepth cohorts change
  // Tumour type for somatic runs. OncoKB evidence levels are per-indication, so
  // this is what makes a returned level attributable to the patient.
  oncotree = null,
  onOncotreeChange,
  // Specimen metadata and other analyses of the patient (roadmap R1.2a), somatic
  // only and optional.
  specimen = EMPTY_SPECIMEN,
  onSpecimenChange,
  linkedAnalyses = [],
  onLinkedAnalysesChange,
  // The RNA library of a tumour RNA analysis (roadmap R4.1).
  library = EMPTY_LIBRARY,
  onLibraryChange,
}) => {
  const theme = useTheme()
  // A tumour RNA analysis (R4.1) has a library instead of a sequencing type.
  const isRna = analysisType === 'Somatic' && isRnaSelection(selectedFiles)
  const [isTargeted, setIsTargeted] = useState(false)
  const [gatkCohorts, setGatkCohorts] = useState([])
  const [defaultCohort, setDefaultCohort] = useState(null)
  const [exomedepthCohorts, setExomedepthCohorts] = useState([])
  const [loadingCohorts, setLoadingCohorts] = useState(false)

  // Cohorts matching the currently selected capture kit
  const matchingGatkCohorts = useMemo(() => {
    if (!Array.isArray(gatkCohorts)) return []
    return gatkCohorts
      .filter((cohort) => {
        const bed = cohort?.bed_file
        if (bed) {
          return bed.name === captureKit || bed.original_name === captureKit
        }
        return cohort?.bed_file_name === captureKit || cohort?.bed_file_original_name === captureKit
      })
      .sort((a, b) => {
        // Priority order: COMPLETED > pending/in-progress > FAILED
        const getStatusPriority = (status) => {
          if (status === 'COMPLETED') return 0
          if (status === 'FAILED') return 2
          return 1 // pending/in-progress
        }

        const aPriority = getStatusPriority(a.status)
        const bPriority = getStatusPriority(b.status)

        if (aPriority !== bPriority) return aPriority - bPriority

        // Secondary sort by name
        return (a.cohort_name || '').localeCompare(b.cohort_name || '')
      })
  }, [gatkCohorts, captureKit])

  const matchingExomeDepthCohorts = useMemo(() => {
    if (!Array.isArray(exomedepthCohorts)) return []
    return exomedepthCohorts
      .filter((cohort) => {
        const bed = cohort?.bed_file
        if (bed) {
          return bed.name === captureKit || bed.original_name === captureKit
        }
        return cohort?.bed_file_name === captureKit || cohort?.bed_file_original_name === captureKit
      })
      .sort((a, b) => {
        // Priority order: COMPLETED > pending/in-progress > FAILED
        const getStatusPriority = (status) => {
          if (status === 'COMPLETED') return 0
          if (status === 'FAILED') return 2
          return 1 // pending/in-progress
        }

        const aPriority = getStatusPriority(a.status)
        const bPriority = getStatusPriority(b.status)

        if (aPriority !== bPriority) return aPriority - bPriority

        // Secondary sort by name
        return (a.cohort_name || '').localeCompare(b.cohort_name || '')
      })
  }, [exomedepthCohorts, captureKit])

  // Determine if selected files are VCF or FASTQ
  const selectedFileTypes = useMemo(() => {
    const primaryFile = files.find((f) => (f.vcf_id || f.fastq_pair_id) === selectedFiles.primary)
    const normalFile = files.find((f) => (f.vcf_id || f.fastq_pair_id) === selectedFiles.normal)

    return {
      primary: primaryFile?.vcf_id ? 'VCF' : 'FASTQ',
      normal: normalFile?.vcf_id ? 'VCF' : 'FASTQ',
      isVCF: !!primaryFile?.vcf_id,
      isFASTQ: !primaryFile?.vcf_id,
    }
  }, [selectedFiles, files])

  const referenceGenomeOptions = [
    { value: 'GRCh38', label: 'GRCh38 (hg38)' },
    { value: 'GRCh37', label: 'GRCh37 (hg19)' },
  ]

  const targetedTypeOptions = [
    { value: 'Clinical Exome', label: 'Clinical Exome' },
    { value: 'Whole Exome', label: 'Whole Exome' },
  ]

  const getAnalysisDescription = () => {
    if (analysisType === 'Germline') {
      return 'Single-sample analysis for identifying inherited genetic variants and hereditary conditions'
    } else if (analysisType === 'Somatic') {
      return 'Tumor vs normal comparison analysis for identifying acquired mutations in cancer'
    }
    return ''
  }

  const getConfigurationSteps = () => {
    if (selectedFileTypes.isVCF) {
      // VCF files: Only reference genome needed
      return ['reference']
    } else {
      // FASTQ files: Only sequencing type needed (no reference genome)
      return ['sequencing']
    }
  }
  // Determine if CNV options should be available
  const showCnvOptions = useMemo(() => {
    const isGermline = analysisType === 'Germline'
    const isFastq = selectedFileTypes.isFASTQ
    const isTargetedSequencing = sequencingType && sequencingType !== 'Whole Genome'
    return isGermline && isFastq && isTargetedSequencing
  }, [analysisType, selectedFileTypes.isFASTQ, sequencingType])

  // When CNV availability changes to false, ensure CNV-related selections are reset
  useEffect(() => {
    if (!showCnvOptions) {
      if (runCnv && onRunCnvChange) onRunCnvChange(false)
      if (cnvMethod !== 'genesus-cnv' && onCnvMethodChange) onCnvMethodChange('genesus-cnv')
      if (selectedCohortId && onSelectedCohortIdChange) onSelectedCohortIdChange(GENESUS_AUTOMATIC_COHORT)
    }
  }, [showCnvOptions, cnvMethod, onCnvMethodChange, onRunCnvChange, onSelectedCohortIdChange, runCnv, selectedCohortId])

  const handleSequencingChoice = (choice) => {
    setIsTargeted(choice === 'targeted')
    if (choice === 'whole-genome') {
      onSequencingTypeChange('Whole Genome')
    } else {
      onSequencingTypeChange('') // Reset for targeted selection
    }
  }

  // Keep local targeted UI state in sync when navigating back with existing selections
  useEffect(() => {
    if (!selectedFileTypes.isFASTQ) {
      setIsTargeted(false)
      return
    }
    if (!sequencingType) {
      setIsTargeted(false)
      return
    }
    // Any non-Whole Genome sequencing implies targeted flow
    setIsTargeted(sequencingType !== 'Whole Genome')
  }, [sequencingType, selectedFileTypes.isFASTQ])

  // Specimen fields are optional, but a value that is there must be valid.
  const specimenErrors = useMemo(
    () => (analysisType === 'Somatic' ? validateSpecimen(specimen) : {}),
    [analysisType, specimen],
  )
  const specimenValid = Object.keys(specimenErrors).length === 0
  const libraryErrors = useMemo(() => (isRna ? validateLibrary(library) : {}), [isRna, library])
  const libraryValid = Object.keys(libraryErrors).length === 0

  // Add validation logic
  const isStepValid = useMemo(() => {
    // Every somatic path needs its tumour type (roadmap R1.19): the backend
    // refuses a somatic analysis without a valid OncoTree code.
    if (analysisType === 'Somatic' && !oncotree?.oncotree_code) {
      return false
    }
    if (!specimenValid) {
      return false
    }
    if (isRna) {
      // RNA takes its library, not a sequencing type or capture kit.
      return libraryValid
    }
    if (selectedFileTypes.isFASTQ) {
      // For FASTQ files: sequencing type is required
      if (!sequencingType) {
        return false
      }

      // If targeted sequencing is selected, capture kit is also required
      if (sequencingType !== 'Whole Genome' && !captureKit) {
        return false
      }

      // For GENESUS-CNV: selectedCohortId === GENESUS_AUTOMATIC_COHORT is the
      // "GENESUS Automatic" option (routes to the on-demand per-case cohort
      // pipeline backend-side). Only an explicit user-picked CUSTOM cohort
      // sets a real cohort id.

      // If CNV is enabled and ExomeDepth method is selected, a real cohort id is required
      if (
        runCnv &&
        cnvMethod === 'exomedepth' &&
        (!selectedCohortId || selectedCohortId === GENESUS_AUTOMATIC_COHORT)
      ) {
        return false
      }

      return true
    }
    return true
  }, [
    selectedFileTypes,
    sequencingType,
    captureKit,
    runCnv,
    cnvMethod,
    selectedCohortId,
    analysisType,
    oncotree,
    specimenValid,
    isRna,
    libraryValid,
  ])

  // Notify parent component about validation state changes
  useEffect(() => {
    if (onValidationChange) {
      onValidationChange(isStepValid)
    }
  }, [isStepValid, onValidationChange])

  // Fetch GATK cohorts when GATK method is selected
  useEffect(() => {
    const loadGatkCohorts = async () => {
      if (runCnv && cnvMethod === 'genesus-cnv') {
        setLoadingCohorts(true)
        try {
          const response = await fetchGatkCohorts()
          if (response.success) {
            const cohorts = response.cohorts || []
            setGatkCohorts(cohorts)
            // Capture the default cohort from response (latest milestone or DEFAULT).
            // NOTE: we deliberately do NOT auto-select defCohort.id here. The
            // "GENESUS Automatic" UI option is represented by the
            // GENESUS_AUTOMATIC_COHORT sentinel (translated to undefined at
            // submission → on-demand per-case cohort pipeline backend-side).
            // Setting defCohort.id here would leak a MILESTONE cohort id onto
            // the wire and route to the legacy cohort-case pipeline.
            const defCohort = response.default_cohort || null
            setDefaultCohort(defCohort)
            if (onDefaultCohortChange) {
              onDefaultCohortChange(defCohort)
            }
            if (onGatkCohortsChange) {
              onGatkCohortsChange(cohorts)
            }
          }
        } catch (error) {
          console.error('Failed to fetch GATK cohorts:', error)
          setGatkCohorts([])
          setDefaultCohort(null)
          if (onDefaultCohortChange) {
            onDefaultCohortChange(null)
          }
          if (onGatkCohortsChange) {
            onGatkCohortsChange([])
          }
        } finally {
          setLoadingCohorts(false)
        }
      }
    }

    loadGatkCohorts()
  }, [runCnv, cnvMethod])

  // Fetch ExomeDepth cohorts when ExomeDepth method is selected
  useEffect(() => {
    const loadExomeDepthCohorts = async () => {
      if (runCnv && cnvMethod === 'exomedepth') {
        setLoadingCohorts(true)
        try {
          const response = await fetchExomeDepthCohorts()
          if (response.success) {
            const cohorts = response.cohorts || []
            setExomedepthCohorts(cohorts)
            if (onExomedepthCohortsChange) {
              onExomedepthCohortsChange(cohorts)
            }
          }
        } catch (error) {
          console.error('Failed to fetch ExomeDepth cohorts:', error)
          setExomedepthCohorts([])
          if (onExomedepthCohortsChange) {
            onExomedepthCohortsChange([])
          }
        } finally {
          setLoadingCohorts(false)
        }
      }
    }

    loadExomeDepthCohorts()
  }, [runCnv, cnvMethod])

  // Keep selected cohort consistent with selected capture kit; clear if mismatched
  useEffect(() => {
    if (cnvMethod !== 'genesus-cnv' || !runCnv) return
    if (!selectedCohortId || selectedCohortId === GENESUS_AUTOMATIC_COHORT) return
    const cohort = gatkCohorts.find((c) => c.id === selectedCohortId)
    if (!cohort) return
    const matches =
      cohort?.bed_file?.name === captureKit ||
      cohort?.bed_file?.original_name === captureKit ||
      cohort?.bed_file_name === captureKit ||
      cohort?.bed_file_original_name === captureKit
    if (!matches && onSelectedCohortIdChange) {
      onSelectedCohortIdChange(GENESUS_AUTOMATIC_COHORT)
    }
  }, [captureKit, cnvMethod, runCnv, selectedCohortId, gatkCohorts, onSelectedCohortIdChange])

  // Keep selected cohort consistent with selected capture kit for ExomeDepth; clear if mismatched
  useEffect(() => {
    if (cnvMethod !== 'exomedepth' || !runCnv) return
    if (!selectedCohortId) return
    const cohort = exomedepthCohorts.find((c) => c.id === selectedCohortId)
    if (!cohort) return
    const matches =
      cohort?.bed_file?.name === captureKit ||
      cohort?.bed_file?.original_name === captureKit ||
      cohort?.bed_file_name === captureKit ||
      cohort?.bed_file_original_name === captureKit
    if (!matches && onSelectedCohortIdChange) {
      onSelectedCohortIdChange('')
    }
  }, [captureKit, cnvMethod, runCnv, selectedCohortId, exomedepthCohorts, onSelectedCohortIdChange])

  const renderReferenceGenomeStep = () => (
    <motion.div variants={childVariants} key="reference">
      <Card
        sx={{
          mb: 2,
          backgroundColor: theme.palette.action.hover,
          borderRadius: 2,
          border: `1px solid ${theme.palette.divider}`,
          overflow: 'hidden',
        }}
      >
        <CardContent sx={{ p: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Box
                sx={{
                  width: 24,
                  height: 24,
                  borderRadius: '50%',
                  backgroundColor: theme.palette.primary.main,
                  color: theme.palette.primary.contrastText,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontWeight: 600,
                  fontSize: '0.8rem',
                }}
              >
                1
              </Box>
              <Typography variant="subtitle1" sx={{ color: theme.palette.primary.main, fontWeight: 600 }}>
                Reference Genome
              </Typography>
            </Box>

            <StyledTextField
              select
              size="small"
              label="Genome Build"
              value={referenceGenome}
              onChange={(e) => onReferenceGenomeChange(e.target.value)}
              variant="outlined"
              sx={{ minWidth: 160 }}
            >
              <MenuItem value="None" disabled>
                Select reference
              </MenuItem>
              {referenceGenomeOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </StyledTextField>
          </Box>
        </CardContent>
      </Card>
    </motion.div>
  )

  // Somatic only, and for ALL THREE somatic paths (uploaded tumour VCF, FASTQ
  // tumour-only, FASTQ tumour-normal): the per-indication problem is a property
  // of OncoKB annotation, not of how the variants were called.
  const renderOncotreeStep = () =>
    analysisType === 'Somatic' ? (
      <motion.div variants={childVariants} key="oncotree">
        <Card
          sx={{
            mb: 2,
            backgroundColor: theme.palette.action.hover,
            borderRadius: 2,
            border: `1px solid ${theme.palette.divider}`,
            overflow: 'hidden',
          }}
        >
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <Typography variant="subtitle1" sx={{ color: theme.palette.primary.main, fontWeight: 600 }}>
                Tumour Type
              </Typography>
            </Box>
            <OncotreeSelect value={oncotree} onChange={onOncotreeChange} required showError />
          </CardContent>
        </Card>
      </motion.div>
    ) : null

  // Somatic only, all three paths: the specimen the tumour (and normal) came from.
  const renderSpecimenStep = () =>
    analysisType === 'Somatic' && onSpecimenChange ? (
      <motion.div variants={childVariants} key="specimen">
        <Card
          sx={{
            mb: 2,
            backgroundColor: theme.palette.action.hover,
            borderRadius: 2,
            border: `1px solid ${theme.palette.divider}`,
            overflow: 'hidden',
          }}
        >
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <Typography variant="subtitle1" sx={{ color: theme.palette.primary.main, fontWeight: 600 }}>
                Specimen and Patient
              </Typography>
            </Box>
            <SpecimenFields
              specimen={specimen}
              onChange={onSpecimenChange}
              errors={specimenErrors}
              paired={selectedFiles?.somaticAnalysisType === 'tumor-normal'}
              linkedAnalyses={linkedAnalyses}
              onLinkedAnalysesChange={onLinkedAnalysesChange}
            />
          </CardContent>
        </Card>
      </motion.div>
    ) : null

  // Somatic FASTQ runs (roadmap R1.25): copy number is not a toggle here. The
  // germline CNV options below never apply to a tumour; every tumour FASTQ run
  // gets the somatic structural stages after alignment instead.
  const renderSomaticStructuralNote = () =>
    analysisType === 'Somatic' && selectedFileTypes.isFASTQ && !isRna ? (
      <motion.div variants={childVariants} key="somatic-structural">
        <Card
          data-testid="somatic-structural-note"
          sx={{
            mb: 2,
            backgroundColor: theme.palette.info.main + '08',
            borderRadius: 2,
            border: `1px solid ${theme.palette.info.main + '30'}`,
            overflow: 'hidden',
          }}
        >
          <CardContent sx={{ p: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
              <Info sx={{ color: theme.palette.info.main, fontSize: 20, mt: 0.25 }} />
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                  Somatic copy number runs automatically
                </Typography>
                <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
                  Every tumour FASTQ run is segmented with GATK ModelSegments in tumour mode. A tumour-normal run is
                  compared with its own matched normal (coverage and heterozygous sites), with no panel of normals
                  needed. A tumour-only run uses a panel of normals built from your organisation&apos;s normal samples
                  of the same kit; with too few normals it uses a flat reference and flags its copy number as low
                  confidence. Structural variants and DNA fusions are called in the same run (on panels and exomes,
                  within the target regions only); a tumour-only run without enough comparable normal samples flags them
                  as low confidence.
                </Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>
      </motion.div>
    ) : null

  // Tumour RNA (roadmap R4.1): the library card, and what the run will produce.
  const renderLibraryStep = () =>
    isRna ? (
      <motion.div variants={childVariants} key="rna-library">
        <Card
          sx={{
            mb: 2,
            backgroundColor: theme.palette.action.hover,
            borderRadius: 2,
            border: `1px solid ${theme.palette.divider}`,
            overflow: 'hidden',
          }}
        >
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <Typography variant="subtitle1" sx={{ color: theme.palette.primary.main, fontWeight: 600 }}>
                RNA Library
              </Typography>
            </Box>
            <LibraryFields
              library={library}
              onChange={onLibraryChange || (() => {})}
              errors={libraryErrors}
              linkedAnalyses={linkedAnalyses}
            />
          </CardContent>
        </Card>
        <Card
          data-testid="rna-run-note"
          sx={{
            mb: 2,
            backgroundColor: theme.palette.info.main + '08',
            borderRadius: 2,
            border: `1px solid ${theme.palette.info.main + '30'}`,
            overflow: 'hidden',
          }}
        >
          <CardContent sx={{ p: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
              <Info sx={{ color: theme.palette.info.main, fontSize: 20, mt: 0.25 }} />
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                  One alignment for fusions and expression
                </Typography>
                <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
                  The RNA is aligned once on GRCh38 and GENCODE v36 (the reference TCGA uses); fusions are called from
                  that alignment and annotated on the same transcript model as DNA fusions, and gene counts are stored
                  for expression. The run is tumour-only: an RNA library has no matched normal.
                </Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>
      </motion.div>
    ) : null

  const renderSequencingTypeStep = () => (
    <motion.div variants={childVariants} key="sequencing">
      <Card
        sx={{
          mb: 2,
          backgroundColor: theme.palette.action.hover,
          borderRadius: 2,
          border: `1px solid ${theme.palette.divider}`,
          overflow: 'hidden',
        }}
      >
        <CardContent sx={{ p: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            <Box
              sx={{
                width: 24,
                height: 24,
                borderRadius: '50%',
                backgroundColor: theme.palette.primary.main,
                color: theme.palette.primary.contrastText,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontWeight: 600,
                fontSize: '0.8rem',
              }}
            >
              1
            </Box>
            <Typography variant="subtitle1" sx={{ color: theme.palette.primary.main, fontWeight: 600 }}>
              Sequencing Type
            </Typography>
          </Box>

          {!isTargeted && !sequencingType && (
            <Box sx={{ display: 'flex', gap: 1.5, mb: 2, flexDirection: { xs: 'column', sm: 'row' } }}>
              <Card
                sx={{
                  flex: 1,
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  border: `1px solid ${theme.palette.divider}`,
                  '&:hover': {
                    borderColor: theme.palette.primary.main,
                    backgroundColor: alpha(theme.palette.primary.main, 0.03),
                    transform: 'translateY(-1px)',
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                  },
                }}
                onClick={() => handleSequencingChoice('targeted')}
              >
                <CardContent sx={{ p: 2, textAlign: 'center' }}>
                  <Typography variant="subtitle2" sx={{ color: theme.palette.primary.main, fontWeight: 600, mb: 0.5 }}>
                    Targeted Sequencing
                  </Typography>
                  <Typography variant="caption" sx={{ color: theme.palette.text.secondary, mb: 1, display: 'block' }}>
                    Clinical/Whole Exome
                  </Typography>
                  <Chip
                    label="Focused"
                    size="small"
                    sx={{ backgroundColor: alpha(theme.palette.primary.main, 0.13), fontSize: '0.75rem', height: 20 }}
                  />
                </CardContent>
              </Card>

              <Card
                sx={{
                  flex: 1,
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  border: `1px solid ${theme.palette.divider}`,
                  '&:hover': {
                    borderColor: theme.palette.primary.main,
                    backgroundColor: alpha(theme.palette.primary.main, 0.03),
                    transform: 'translateY(-1px)',
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                  },
                }}
                onClick={() => handleSequencingChoice('whole-genome')}
              >
                <CardContent sx={{ p: 2, textAlign: 'center' }}>
                  <Typography variant="subtitle2" sx={{ color: theme.palette.primary.main, fontWeight: 600, mb: 0.5 }}>
                    Whole Genome
                  </Typography>
                  <Typography variant="caption" sx={{ color: theme.palette.text.secondary, mb: 1, display: 'block' }}>
                    Complete Coverage
                  </Typography>
                  <Chip
                    label="Comprehensive"
                    size="small"
                    sx={{ backgroundColor: alpha(theme.palette.success.main, 0.13), fontSize: '0.75rem', height: 20 }}
                  />
                </CardContent>
              </Card>
            </Box>
          )}

          {isTargeted && (
            <AnimatePresence mode="wait">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.3 }}
              >
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 2, flexWrap: 'wrap' }}>
                  <Typography variant="body2" sx={{ fontWeight: 600, minWidth: 'fit-content' }}>
                    Targeted Type:
                  </Typography>
                  <StyledTextField
                    select
                    size="small"
                    label="Type"
                    value={sequencingType}
                    onChange={(e) => onSequencingTypeChange(e.target.value)}
                    variant="outlined"
                    sx={{ minWidth: 180, flex: 1 }}
                  >
                    {targetedTypeOptions.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </StyledTextField>
                </Box>

                {sequencingType && (
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3 }}
                  >
                    <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                      <Typography variant="body2" sx={{ fontWeight: 600, minWidth: 'fit-content' }}>
                        Capture Kit:
                      </Typography>
                      <StyledTextField
                        select
                        size="small"
                        label="Kit"
                        value={captureKit}
                        onChange={(e) => onCaptureKitChange(e.target.value)}
                        variant="outlined"
                        sx={{ minWidth: 180, flex: 1 }}
                      >
                        {bedFiles.map((bedFile) => {
                          const isProcessed = bedFile.is_processed !== false
                          const isVerified = bedFile.is_verified !== false
                          const isDisabled = !isProcessed || !isVerified

                          let tooltipTitle = ''
                          let warningIcon = null

                          if (!isProcessed) {
                            tooltipTitle = 'Verifying...'
                            warningIcon = (
                              <Warning
                                sx={{
                                  color: theme.palette.warning.main,
                                  fontSize: 16,
                                  ml: 'auto',
                                }}
                              />
                            )
                          } else if (!isVerified) {
                            tooltipTitle = 'Unverified'
                            warningIcon = (
                              <Warning
                                sx={{
                                  color: theme.palette.warning.main,
                                  fontSize: 16,
                                  ml: 'auto',
                                }}
                              />
                            )
                          }

                          const menuItem = (
                            <MenuItem
                              key={bedFile.name}
                              value={bedFile.name}
                              disabled={isDisabled}
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                                opacity: isDisabled ? 0.5 : 1,
                                '&.Mui-disabled': {
                                  opacity: 0.5,
                                },
                              }}
                            >
                              {bedFile.visible_name}
                              {warningIcon}
                            </MenuItem>
                          )

                          return tooltipTitle ? (
                            <Tooltip
                              key={bedFile.name}
                              title={tooltipTitle}
                              placement="right"
                              disableHoverListener={false}
                            >
                              <span>{menuItem}</span>
                            </Tooltip>
                          ) : (
                            menuItem
                          )
                        })}
                      </StyledTextField>
                    </Box>
                  </motion.div>
                )}
              </motion.div>
            </AnimatePresence>
          )}

          {isTargeted && showCnvOptions && (
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}>
              <Card
                sx={{
                  mt: 2,
                  backgroundColor: runCnv ? alpha(theme.palette.info.main, 0.03) : theme.palette.background.paper,
                  borderRadius: 2,
                  border: `1px solid ${runCnv ? alpha(theme.palette.info.main, 0.19) : theme.palette.divider}`,
                  overflow: 'hidden',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    borderColor: runCnv ? theme.palette.info.main : alpha(theme.palette.primary.main, 0.31),
                    backgroundColor: runCnv
                      ? alpha(theme.palette.info.main, 0.07)
                      : alpha(theme.palette.primary.main, 0.02),
                  },
                }}
              >
                <CardContent sx={{ p: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Box
                      sx={{
                        width: 20,
                        height: 20,
                        borderRadius: '50%',
                        backgroundColor: runCnv ? theme.palette.info.main : theme.palette.action.disabled,
                        color: theme.palette.info.contrastText,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '0.75rem',
                        fontWeight: 600,
                        transition: 'all 0.2s ease',
                      }}
                    >
                      {runCnv ? '✓' : '2'}
                    </Box>

                    <Box sx={{ flex: 1 }}>
                      <Typography
                        variant="subtitle2"
                        sx={{ color: theme.palette.text.primary, fontWeight: 600, mb: 0.5 }}
                      >
                        CNV Analysis
                      </Typography>
                      <Typography variant="caption" sx={{ color: theme.palette.text.secondary, display: 'block' }}>
                        Copy Number Variation detection for structural variants
                      </Typography>
                    </Box>

                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={runCnv}
                          onChange={(e) => onRunCnvChange && onRunCnvChange(e.target.checked)}
                          color="primary"
                          sx={{
                            '&.Mui-checked': {
                              color: theme.palette.info.main,
                            },
                          }}
                        />
                      }
                      label=""
                      sx={{ m: 0 }}
                    />
                  </Box>

                  {runCnv && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      exit={{ opacity: 0, height: 0 }}
                      transition={{ duration: 0.2 }}
                    >
                      <Box
                        sx={{
                          mt: 1.5,
                          p: 1.5,
                          backgroundColor: alpha(theme.palette.info.main, 0.08),
                          borderRadius: 1,
                          border: `1px solid ${alpha(theme.palette.info.main, 0.13)}`,
                        }}
                      >
                        <Typography
                          variant="body2"
                          sx={{
                            color: theme.palette.info.main,
                            fontWeight: 600,
                            fontSize: '0.8rem',
                            mb: 1.5,
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                          }}
                        >
                          <Box
                            sx={{
                              width: 6,
                              height: 6,
                              borderRadius: '50%',
                              backgroundColor: theme.palette.info.main,
                            }}
                          />
                          CNV analysis will be included in this run
                        </Typography>

                        {/* Exome CNV cohort requirement notice */}
                        <Box
                          sx={{
                            mb: 1.5,
                            p: 1.25,
                            borderRadius: 1,
                            backgroundColor: alpha(theme.palette.warning.main, 0.07),
                            border: `1px solid ${alpha(theme.palette.warning.main, 0.25)}`,
                            display: 'flex',
                            gap: 1,
                            alignItems: 'flex-start',
                          }}
                        >
                          <Warning
                            style={{
                              color: theme.palette.warning.main,
                              fontSize: 18,
                              marginTop: 1,
                              flexShrink: 0,
                            }}
                          />
                          <Box sx={{ flex: 1 }}>
                            <Typography
                              variant="body2"
                              sx={{
                                fontSize: '0.78rem',
                                fontWeight: 600,
                                color: theme.palette.warning.main,
                                mb: 0.5,
                              }}
                            >
                              Exome CNV calling needs a reference cohort
                            </Typography>
                            <Typography
                              variant="caption"
                              sx={{
                                fontSize: '0.75rem',
                                color: theme.palette.text.primary,
                                display: 'block',
                                lineHeight: 1.5,
                              }}
                            >
                              Exome capture introduces per-target coverage bias, so CNV callers need a panel of
                              similarly-prepared normal samples to separate real copy-number changes from capture noise.
                              Without enough matched samples, calls will be noisy and false-positive-heavy.
                            </Typography>
                            <Box component="ul" sx={{ pl: 2, mt: 0.75, mb: 0, '& li': { mb: 0.25 } }}>
                              <Typography
                                component="li"
                                variant="caption"
                                sx={{
                                  fontSize: '0.75rem',
                                  color: theme.palette.text.primary,
                                  lineHeight: 1.5,
                                }}
                              >
                                <b>Best:</b> build a capture-kit-specific cohort from the “Cohort Creation” section in
                                analysis type selection with 30+ samples on the same kit, then pick it under{' '}
                                <i>Finished Cohort</i>.
                              </Typography>
                              <Typography
                                component="li"
                                variant="caption"
                                sx={{
                                  fontSize: '0.75rem',
                                  color: theme.palette.text.primary,
                                  lineHeight: 1.5,
                                }}
                              >
                                <b>GENESUS Automatic:</b> we assemble the cohort on the fly from your organisation’s
                                samples on the same capture kit. It won’t produce a meaningful cohort if your
                                organisation doesn’t have enough similar samples yet.
                              </Typography>
                            </Box>
                          </Box>
                        </Box>

                        {/* CNV Method Selection */}
                        <FormControl component="fieldset" sx={{ mb: 1.5 }}>
                          <FormLabel
                            component="legend"
                            sx={{
                              fontSize: '0.8rem',
                              fontWeight: 600,
                              color: theme.palette.text.primary,
                              mb: 1,
                            }}
                          >
                            CNV Method
                          </FormLabel>
                          <RadioGroup
                            value={cnvMethod}
                            onChange={(e) => onCnvMethodChange && onCnvMethodChange(e.target.value)}
                            sx={{ gap: 0.5 }}
                          >
                            <FormControlLabel
                              value="genesus-cnv"
                              control={<Radio size="small" />}
                              label={
                                <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
                                  <Typography variant="body2" sx={{ fontSize: '0.8rem', position: 'relative' }}>
                                    GENESUS
                                    <sup style={{ fontSize: '0.6em', verticalAlign: 'top', marginLeft: '2px' }}>™</sup>
                                    {' CNV'}
                                  </Typography>
                                  <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
                                    v1.0.0
                                  </Typography>
                                  <Chip
                                    label="Recommended"
                                    size="small"
                                    sx={{
                                      fontSize: '0.75rem',
                                      height: 18,
                                      backgroundColor: alpha(theme.palette.success.main, 0.13),
                                      color: theme.palette.success.main,
                                    }}
                                  />{' '}
                                </Box>
                              }
                              sx={{ m: 0 }}
                            />
                            <FormControlLabel
                              value="ecole"
                              control={<Radio size="small" />}
                              label={
                                <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
                                  <Typography variant="body2" sx={{ fontSize: '0.8rem', position: 'relative' }}>
                                    Ecole
                                    <sup style={{ fontSize: '0.6em', verticalAlign: 'top', marginLeft: '2px' }}>™</sup>
                                  </Typography>
                                  <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
                                    v0.2
                                  </Typography>
                                </Box>
                              }
                              sx={{ m: 0 }}
                            />
                            <FormControlLabel
                              value="exomedepth"
                              control={<Radio size="small" />}
                              label={
                                <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
                                  <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                                    ExomeDepth
                                  </Typography>
                                  <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
                                    v1.1.16
                                  </Typography>
                                </Box>
                              }
                              sx={{ m: 0 }}
                            />
                          </RadioGroup>
                        </FormControl>

                        {/* GATK Cohort Selection */}
                        {cnvMethod === 'genesus-cnv' && (
                          <motion.div
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -10 }}
                            transition={{ duration: 0.2 }}
                          >
                            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                              <Typography
                                variant="body2"
                                sx={{ fontWeight: 600, minWidth: 'fit-content', fontSize: '0.8rem' }}
                              >
                                Finished Cohort:
                              </Typography>
                              <StyledTextField
                                select
                                size="small"
                                label="Select Cohort"
                                value={selectedCohortId}
                                onChange={(e) => onSelectedCohortIdChange && onSelectedCohortIdChange(e.target.value)}
                                variant="outlined"
                                disabled={loadingCohorts}
                                sx={{ minWidth: 200, flex: 1 }}
                              >
                                {loadingCohorts ? (
                                  <MenuItem value="" disabled>
                                    Loading cohorts...
                                  </MenuItem>
                                ) : (
                                  // Always render "GENESUS Automatic" as the first option —
                                  // sentinel for the on-demand per-case cohort pipeline.
                                  // Then matching user-built CUSTOM cohorts.
                                  (() => {
                                    const renderCohortItem = (cohort, displayName = null) => {
                                      if (!cohort || !cohort.id) return null

                                      const status = cohort.status
                                      const isDone = status === 'COMPLETED'
                                      const isFailed = status === 'FAILED'
                                      const isInProgress = !isDone && !isFailed
                                      const isDisabled = !isDone

                                      let warningChip = null
                                      if (isFailed) {
                                        warningChip = (
                                          <Chip
                                            label="Failed"
                                            size="small"
                                            sx={{
                                              fontSize: '0.75rem',
                                              height: 20,
                                              backgroundColor: alpha(theme.palette.error.main, 0.13),
                                              color: theme.palette.error.main,
                                              border: `1px solid ${alpha(theme.palette.error.main, 0.25)}`,
                                            }}
                                          />
                                        )
                                      } else if (isInProgress) {
                                        warningChip = (
                                          <Chip
                                            label={
                                              status
                                                ? status.charAt(0).toUpperCase() + status.slice(1).toLowerCase()
                                                : 'In Progress'
                                            }
                                            size="small"
                                            sx={{
                                              fontSize: '0.75rem',
                                              height: 20,
                                              backgroundColor: alpha(theme.palette.warning.main, 0.13),
                                              color: theme.palette.warning.main,
                                              border: `1px solid ${alpha(theme.palette.warning.main, 0.25)}`,
                                            }}
                                          />
                                        )
                                      } else if (isDone) {
                                        warningChip = (
                                          <Chip
                                            label="Ready"
                                            size="small"
                                            icon={
                                              <CheckCircle
                                                style={{ color: theme.palette.success.main, fontSize: 14 }}
                                              />
                                            }
                                            sx={{
                                              fontSize: '0.75rem',
                                              height: 20,
                                              backgroundColor: alpha(theme.palette.success.main, 0.06),
                                              color: theme.palette.success.main,
                                              border: `1px solid ${alpha(theme.palette.success.main, 0.19)}`,
                                              '& .MuiChip-icon': {
                                                marginLeft: '4px',
                                              },
                                            }}
                                          />
                                        )
                                      }

                                      return (
                                        <MenuItem
                                          key={cohort.id}
                                          value={cohort.id}
                                          disabled={isDisabled}
                                          sx={{
                                            opacity: isDisabled ? 0.6 : 1,
                                            '&.Mui-disabled': {
                                              opacity: 0.6,
                                            },
                                          }}
                                        >
                                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                                            <Typography variant="body2" sx={{ flex: 1 }}>
                                              {displayName || cohort.cohort_name}
                                            </Typography>
                                            {warningChip}
                                          </Box>
                                        </MenuItem>
                                      )
                                    }

                                    const items = []

                                    // "GENESUS Automatic" sentinel — routes the submission
                                    // to the backend on-demand pipeline (cnv_pipeline_wrapper
                                    // builds a fresh per-case cohort when cohort_id is
                                    // absent). Always available.
                                    items.push(
                                      <MenuItem key="genesus-automatic" value={GENESUS_AUTOMATIC_COHORT}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                                          <Typography variant="body2" sx={{ flex: 1 }}>
                                            GENESUS Automatic
                                          </Typography>
                                          <Chip
                                            label="On-demand"
                                            size="small"
                                            icon={
                                              <CheckCircle
                                                style={{ color: theme.palette.success.main, fontSize: 14 }}
                                              />
                                            }
                                            sx={{
                                              fontSize: '0.75rem',
                                              height: 20,
                                              backgroundColor: alpha(theme.palette.success.main, 0.06),
                                              color: theme.palette.success.main,
                                              border: `1px solid ${alpha(theme.palette.success.main, 0.19)}`,
                                              '& .MuiChip-icon': { marginLeft: '4px' },
                                            }}
                                          />
                                        </Box>
                                      </MenuItem>,
                                    )

                                    // Add user's matching CUSTOM cohorts (if any)
                                    matchingGatkCohorts.forEach((cohort) => {
                                      // Skip if this is the same as the default cohort
                                      if (defaultCohort && cohort.id === defaultCohort.id) return
                                      items.push(renderCohortItem(cohort))
                                    })

                                    return items
                                  })()
                                )}
                              </StyledTextField>
                            </Box>
                            {!loadingCohorts && gatkCohorts.length > 0 && matchingGatkCohorts.length === 0 && (
                              <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
                                No user-built cohorts match the selected capture kit: GENESUS Automatic (on-demand) will
                                be used.
                              </Typography>
                            )}
                          </motion.div>
                        )}

                        {/* ExomeDepth Cohort Selection */}
                        {cnvMethod === 'exomedepth' && (
                          <motion.div
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -10 }}
                            transition={{ duration: 0.2 }}
                          >
                            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                              <Typography
                                variant="body2"
                                sx={{ fontWeight: 600, minWidth: 'fit-content', fontSize: '0.8rem' }}
                              >
                                Finished Cohort:
                              </Typography>
                              <StyledTextField
                                select
                                size="small"
                                label="Select Cohort"
                                value={selectedCohortId}
                                onChange={(e) => onSelectedCohortIdChange && onSelectedCohortIdChange(e.target.value)}
                                variant="outlined"
                                disabled={loadingCohorts}
                                sx={{ minWidth: 200, flex: 1 }}
                              >
                                {loadingCohorts ? (
                                  <MenuItem value="" disabled>
                                    Loading cohorts...
                                  </MenuItem>
                                ) : exomedepthCohorts.length === 0 ? (
                                  <MenuItem value="" disabled>
                                    No cohorts available
                                  </MenuItem>
                                ) : (
                                  // Only show cohorts matching the selected capture kit (bed file)
                                  (matchingExomeDepthCohorts.length === 0
                                    ? [
                                        <MenuItem key="no-match" value="" disabled>
                                          No cohorts match the selected capture kit
                                        </MenuItem>,
                                      ]
                                    : matchingExomeDepthCohorts
                                  ).map((cohort) => {
                                    if (!cohort || !cohort.id) return cohort

                                    // For ExomeDepth, use the direct status field from the cohort model
                                    const status = cohort.status
                                    const isDone = status === 'COMPLETED'
                                    const isFailed = status === 'FAILED'
                                    const isInProgress = !isDone && !isFailed
                                    const isDisabled = !isDone

                                    let warningChip = null
                                    if (isFailed) {
                                      warningChip = (
                                        <Chip
                                          label="Failed"
                                          size="small"
                                          sx={{
                                            fontSize: '0.75rem',
                                            height: 20,
                                            backgroundColor: alpha(theme.palette.error.main, 0.13),
                                            color: theme.palette.error.main,
                                            border: `1px solid ${alpha(theme.palette.error.main, 0.25)}`,
                                          }}
                                        />
                                      )
                                    } else if (isInProgress) {
                                      warningChip = (
                                        <Chip
                                          label={
                                            status
                                              ? status.charAt(0).toUpperCase() + status.slice(1).toLowerCase()
                                              : 'In Progress'
                                          }
                                          size="small"
                                          sx={{
                                            fontSize: '0.75rem',
                                            height: 20,
                                            backgroundColor: alpha(theme.palette.warning.main, 0.13),
                                            color: theme.palette.warning.main,
                                            border: `1px solid ${alpha(theme.palette.warning.main, 0.25)}`,
                                          }}
                                        />
                                      )
                                    } else if (isDone) {
                                      warningChip = (
                                        <Chip
                                          label="Ready"
                                          size="small"
                                          icon={
                                            <CheckCircle style={{ color: theme.palette.success.main, fontSize: 14 }} />
                                          }
                                          sx={{
                                            fontSize: '0.75rem',
                                            height: 20,
                                            backgroundColor: alpha(theme.palette.success.main, 0.06),
                                            color: theme.palette.success.main,
                                            border: `1px solid ${alpha(theme.palette.success.main, 0.19)}`,
                                            '& .MuiChip-icon': {
                                              marginLeft: '4px',
                                            },
                                          }}
                                        />
                                      )
                                    }

                                    return (
                                      <MenuItem
                                        key={cohort.id}
                                        value={cohort.id}
                                        disabled={isDisabled}
                                        sx={{
                                          opacity: isDisabled ? 0.6 : 1,
                                          '&.Mui-disabled': {
                                            opacity: 0.6,
                                          },
                                        }}
                                      >
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                                          <Typography variant="body2" sx={{ flex: 1 }}>
                                            {cohort.cohort_name}
                                          </Typography>
                                          {warningChip}
                                        </Box>
                                      </MenuItem>
                                    )
                                  })
                                )}
                              </StyledTextField>
                              {!loadingCohorts &&
                                exomedepthCohorts.length > 0 &&
                                matchingExomeDepthCohorts.length === 0 && (
                                  <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
                                    No cohorts found for the selected capture kit. Choose a different kit or create a
                                    new finished cohort.
                                  </Typography>
                                )}
                            </Box>
                          </motion.div>
                        )}
                      </Box>
                    </motion.div>
                  )}
                </CardContent>
              </Card>
            </motion.div>
          )}

          {sequencingType === 'Whole Genome' && (
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}>
              <Box
                sx={{
                  p: 1.5,
                  backgroundColor: alpha(theme.palette.success.main, 0.03),
                  borderRadius: 1,
                  border: `1px solid ${alpha(theme.palette.success.main, 0.19)}`,
                }}
              >
                <Typography
                  variant="body2"
                  sx={{ color: theme.palette.success.main, fontWeight: 600, fontSize: '0.85rem' }}
                >
                  ✓ Whole genome sequencing selected
                </Typography>
              </Box>
            </motion.div>
          )}

          {(isTargeted || sequencingType === 'Whole Genome') && (
            <Box sx={{ mt: 1.5, display: 'flex', justifyContent: 'flex-end' }}>
              <Chip
                label="Change"
                variant="outlined"
                size="small"
                onClick={() => {
                  setIsTargeted(false)
                  onSequencingTypeChange('')
                  onCaptureKitChange('')
                }}
                sx={{ cursor: 'pointer', height: 24, fontSize: '0.75rem' }}
              />
            </Box>
          )}
        </CardContent>
      </Card>
    </motion.div>
  )

  const renderSummary = () => (
    <motion.div variants={childVariants} key="summary">
      {(referenceGenome !== 'None' || analysisType !== 'Germline') && (
        <Card
          sx={{
            backgroundColor: alpha(theme.palette.primary.main, 0.03),
            borderRadius: 2,
            border: `1px solid ${alpha(theme.palette.primary.main, 0.19)}`,
            overflow: 'hidden',
          }}
        >
          <CardContent sx={{ p: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1.5 }}>
              <Chip
                label={analysisType}
                size="small"
                sx={{
                  backgroundColor: alpha(
                    analysisType === 'Germline' ? theme.palette.success.main : theme.palette.info.main,
                    0.14,
                  ),
                  color:
                    theme.palette.mode === 'light'
                      ? (analysisType === 'Germline' ? theme.palette.success : theme.palette.info).dark
                      : (analysisType === 'Germline' ? theme.palette.success : theme.palette.info).light,
                  fontWeight: 600,
                  height: 24,
                }}
              />
              <Typography variant="subtitle1" sx={{ color: theme.palette.text.primary, fontWeight: 600 }}>
                Configuration Summary
              </Typography>
            </Box>

            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
              {selectedFileTypes.isVCF && (
                <Chip
                  label={`${analysisType} • ${referenceGenome}`}
                  variant="outlined"
                  size="small"
                  sx={{ fontSize: '0.75rem', height: 24 }}
                />
              )}
              {selectedFileTypes.isFASTQ && (
                <Chip
                  label={`${analysisType} Analysis`}
                  variant="outlined"
                  size="small"
                  sx={{ fontSize: '0.75rem', height: 24 }}
                />
              )}
              <Chip
                label={`${selectedFileTypes.primary} files`}
                variant="outlined"
                size="small"
                sx={{ fontSize: '0.75rem', height: 24 }}
              />
              {selectedFileTypes.isFASTQ && sequencingType && (
                <Chip label={sequencingType} variant="outlined" size="small" sx={{ fontSize: '0.75rem', height: 24 }} />
              )}
              {captureKit && (
                <Chip label={captureKit} variant="outlined" size="small" sx={{ fontSize: '0.75rem', height: 24 }} />
              )}
              {captureKit && runCnv && (
                <Chip
                  label="CNV Analysis"
                  variant="outlined"
                  size="small"
                  sx={{
                    fontSize: '0.75rem',
                    height: 24,
                    backgroundColor: alpha(theme.palette.info.main, 0.13),
                    borderColor: theme.palette.info.main,
                    color: theme.palette.info.main,
                  }}
                />
              )}
            </Box>
          </CardContent>
        </Card>
      )}
    </motion.div>
  )

  return (
    <motion.div variants={pageVariants} initial="initial" animate="animate" exit="exit">
      {analysisType !== 'Somatic' && (
        <motion.div variants={childVariants}>
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <Typography variant="h6" sx={{ color: theme.palette.text.primary, fontWeight: 600, mb: 1 }}>
              Configure {analysisType} Analysis
            </Typography>
            <Typography variant="body2" sx={{ color: theme.palette.text.secondary, maxWidth: '600px', mx: 'auto' }}>
              {getAnalysisDescription()}
            </Typography>
          </Box>
        </motion.div>
      )}

      <AnimatePresence mode="wait">
        {selectedFileTypes.isVCF && renderReferenceGenomeStep()}
        {selectedFileTypes.isFASTQ && !isRna && renderSequencingTypeStep()}
        {renderLibraryStep()}
        {/* Somatic only, and independent of input type: a tumour VCF upload and a
            FASTQ tumour-only/tumour-normal run all need the indication before
            OncoKB levels mean anything for this patient. */}
        {renderOncotreeStep()}
        {renderSpecimenStep()}
        {renderSomaticStructuralNote()}
        {(selectedFileTypes.isVCF && referenceGenome) || (selectedFileTypes.isFASTQ && sequencingType && !isRna)
          ? renderSummary()
          : null}
      </AnimatePresence>
    </motion.div>
  )
}

export default StepConfigure
